// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Box, Button, Loader, Paper, ScrollArea, Stack, Text, Title } from '@mantine/core';

import type { JSX } from 'react';
import { useNavigate } from 'react-router';
import { AppointmentHistoryTable } from '../components/appointments/AppointmentHistoryTable';
import { UpcomingAppointmentsTable } from '../components/appointments/UpcomingAppointmentsTable';
import { useAppointmentDetails } from '../hooks/useAppointmentDetails';
import { usePatientAppointments } from '../hooks/usePatientAppointments';
import { useSchedulingEligibility } from '../hooks/useSchedulingEligibility';

/**
 * Patient-facing appointments page.
 * Displays upcoming appointments, appointment history with document links,
 * and a scheduling CTA.
 * @returns The appointments page component
 */
export function AppointmentsPage(): JSX.Element {
  const navigate = useNavigate();
  const { upcoming, history, loading: appointmentsLoading, refresh: refreshAppointments } = usePatientAppointments();
  const { eligibility, loading: eligibilityLoading } = useSchedulingEligibility();
  const { details: upcomingDetails, loading: upcomingEnriching } = useAppointmentDetails(upcoming);
  const { details: historyDetails, loading: historyEnriching } = useAppointmentDetails(history);

  const loading = appointmentsLoading || eligibilityLoading;

  if (loading) {
    return (
      <Box p="lg" bg="#f8f9fb" mih="100%">
        <Stack align="center" py="xl">
          <Loader />
          <Text c="dimmed">Loading appointments...</Text>
        </Stack>
      </Box>
    );
  }

  return (
    <Stack gap="lg" p="lg" bg="#f8f9fb" mih="100%">
      <Paper shadow="sm" withBorder p="lg" radius="md" bg="white">
        <Title order={1} size="2rem" fw={700}>
          Appointments
        </Title>
      </Paper>

      <Paper shadow="sm" withBorder p="lg" radius="md" bg="white">
        <ScrollArea
          scrollbarSize={6}
          type="auto"
          styles={{
            thumb: { backgroundColor: 'var(--mantine-color-polaris-5)' },
          }}
        >
          <div style={{ minWidth: 800, paddingBottom: '1rem' }}>
            {eligibility?.canSchedule && (
              <Button mt="lg" size="md" onClick={() => navigate('/appointments/schedule')}>
                Schedule Next Appointment
              </Button>
            )}
            {!eligibility?.canSchedule && eligibility?.reason && (
              <Text mt="lg" size="sm" c="dimmed">
                {eligibility.reason}
              </Text>
            )}
          </div>
        </ScrollArea>
      </Paper>

      <Paper shadow="sm" withBorder p="lg" radius="md" bg="white">
        <Title order={3} mb="sm" size="h2">
          Upcoming Appointments
        </Title>
        <ScrollArea
          scrollbarSize={6}
          type="auto"
          styles={{
            thumb: { backgroundColor: 'var(--mantine-color-polaris-5)' },
          }}
        >
          <Box pb="md">
            <UpcomingAppointmentsTable
              appointments={upcomingDetails}
              loading={upcomingEnriching}
              onRefresh={refreshAppointments}
            />
          </Box>
        </ScrollArea>
      </Paper>

      <Paper shadow="sm" withBorder p="lg" radius="md" bg="white">
        <Title order={3} mb="sm" size="h2">
          Appointment History
        </Title>
        <ScrollArea
          scrollbarSize={6}
          type="auto"
          styles={{
            thumb: { backgroundColor: 'var(--mantine-color-polaris-5)' },
          }}
        >
          <Box pb="md">
            <AppointmentHistoryTable appointments={historyDetails} loading={historyEnriching} />
          </Box>
        </ScrollArea>
      </Paper>
    </Stack>
  );
}
